// Paying bills. "Pay with Wise": per bill a USD quote and a transfer to the vendor's Wise recipient with the reference
// "<vendor> <PO> <bill>" (or all of them in one Wise batch); then someone approves and funds them in the Wise app (HHL
// Ops never funds). The ids are made here, so clicking again (or Retry) never makes a second transfer. "Mark paid
// outside": a payment that already happened (card, wire, other, or a transfer made in the Wise app), with its receipt.

import { AlertTriangle, ExternalLink, Lock } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  billMatch,
  billState,
  MATCH_LABELS,
  OUTSIDE_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
  type PaymentMethod,
  type PaymentStatus,
  referenceForBill,
  vendorLabelOf,
} from "../../../../shared/bills.ts";
import { numberOf } from "../../../../shared/calc.ts";
import { planToday } from "../../../../shared/day.ts";
import { amount, postJson, useBillIndex, useWiseConnection, wiseFeature } from "../../lib/bills.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { uploadAttachment } from "../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, Textarea } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { FileDrop } from "../../ui/FileDrop.tsx";
import { toast } from "../../ui/toast.tsx";
import { wiseLink } from "./DueBoard.tsx";
import { PaidWith } from "./PaidWith.tsx";

export function Dialog({
  title,
  sub,
  aside,
  onClose,
  children,
  width = 680,
}: {
  title: string;
  sub?: ReactNode;
  aside?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()} onKeyDown={(e) => e.key === "Escape" && onClose()} role="presentation">
      <div className="rounded-xl border border-line bg-surface p-4 shadow-pop" style={{ width: `min(${width}px, calc(100vw - 32px))` }}>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-[15px] font-semibold">{title}</div>
          {aside}
        </div>
        {sub && <div className="mt-0.5 text-[12px] text-muted">{sub}</div>}
        {children}
      </div>
    </DialogLayer>
  );
}

type Outcome = {
  payment_id: string;
  number: string;
  bill: string;
  amount: number;
  reference: string;
  status: PaymentStatus;
  transfer_id: number | null;
  url: string | null;
  error: string;
};

export function PayDialog({ billIds: picked, onClose }: { billIds: string[]; onClose: (done: boolean) => void }) {
  const ix = useBillIndex();
  // The bills as they were picked: paying moves them off the board while this is still open.
  const [billIds] = useState(picked);
  const conn = useWiseConnection();
  const wise = wiseFeature();
  // One payment id per bill, made once: Retry and a second click send the same ids (Wise's customerTransactionId).
  const [ids] = useState(() => new Map(billIds.map((b) => [b, crypto.randomUUID()])));
  const [batch, setBatch] = useState(billIds.length > 1);
  const [batchId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Outcome[] | null>(null);
  // Table views --- the bills' View and Columns menus, beside the title
  const rows = billIds.map((id, order) => {
    const bill = ix.billById.get(id);
    const recipient = bill ? ix.recipientByVendor.get(bill.vendor_id) : undefined;
    const st = billState(ix, id);
    const m = billMatch(ix, id);
    const problem = !bill
      ? "This bill is gone"
      : bill.currency !== "USD"
        ? `In ${bill.currency}: Wise pays USD only for now; mark it paid outside`
        : !recipient
          ? "The vendor has no Wise recipient yet"
          : st.due <= 0
            ? "Nothing left to pay"
            : null;
    return {
      id,
      order,
      bill,
      vendor: vendorLabelOf(ix.vendorById.get(bill?.vendor_id ?? "")),
      recipient,
      due: st.due,
      match: m,
      problem,
      reference: bill ? referenceForBill(ix, id) : "",
    };
  });
  type Row = (typeof rows)[number] & { out: Outcome | undefined };
  const matchWarning = (r: Row) =>
    !r.out && r.match.diffs.length > 0 && r.match.status !== "accepted"
      ? `3-way match: ${MATCH_LABELS[r.match.status].toLowerCase()} (${r.match.diffs[0].text}): check before paying`
      : "";
  const payColumns: Column<Row>[] = [
    {
      key: "order",
      header: "Bill",
      width: "96px",
      text: (r) => r.bill?.number ?? "",
      cell: (r) => <span className="font-mono text-[12px] font-medium">{r.bill?.number}</span>,
    },
    { key: "vendor", header: "Vendor", width: "minmax(120px,1fr)", cell: (r) => <span className="truncate font-medium">{r.vendor}</span> },
    {
      key: "invoice",
      header: "Their invoice",
      width: "minmax(100px,1fr)",
      optional: true,
      value: (r) => r.bill?.invoice_number ?? "",
      cell: (r) => <span className="truncate font-mono text-[12px] text-muted">{r.bill?.invoice_number}</span>,
    },
    {
      key: "to",
      header: "To",
      width: "minmax(140px,1fr)",
      value: (r) => (r.recipient ? `${r.recipient.name}${r.recipient.last4 ? ` ···· ${r.recipient.last4}` : ""}` : ""),
      cell: (r) =>
        r.recipient ? (
          <span className="truncate text-[12px] text-muted">
            To {r.recipient.name}
            {r.recipient.last4 ? ` ···· ${r.recipient.last4}` : ""}
          </span>
        ) : (
          <CellControls className="text-[12px]">
            <Link to={`/vendors?open=${r.bill?.vendor_id}`} className="truncate text-warn underline">
              Link the vendor to its Wise recipient first
            </Link>
          </CellControls>
        ),
    },
    {
      key: "reference",
      header: "Reference",
      width: "minmax(160px,1.3fr)",
      value: (r) => (r.recipient ? r.out?.reference || r.reference : ""),
      cell: (r) =>
        r.recipient && (
          <span className="truncate font-mono text-[12px]" title={r.out?.reference || r.reference}>
            {r.out?.reference || r.reference}
          </span>
        ),
    },
    {
      key: "match",
      header: "3-way match",
      width: "minmax(130px,1fr)",
      value: (r) => matchWarning(r),
      cell: (r) =>
        matchWarning(r) && (
          <span className="flex min-w-0 items-center gap-1 text-[12px] text-warn" title={matchWarning(r)}>
            <AlertTriangle className="size-3 shrink-0" />
            <span className="truncate">{matchWarning(r)}</span>
          </span>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "minmax(150px,1fr)",
      value: (r) => (r.out ? PAYMENT_STATUS_LABELS[r.out.status] : (r.problem ?? "Ready")),
      cell: (r) =>
        r.out ? (
          <span className="flex min-w-0 items-center gap-1.5 text-[12px]">
            <Badge tone={PAYMENT_STATUS_TONE[r.out.status]}>{PAYMENT_STATUS_LABELS[r.out.status]}</Badge>
            <span className="font-mono text-muted">{r.out.number}</span>
          </span>
        ) : r.problem ? (
          <span className="truncate text-[12px] text-warn" title={r.problem}>
            {r.problem}
          </span>
        ) : (
          <span className="text-[12px] text-muted">Ready</span>
        ),
    },
    ...(results
      ? [
          {
            key: "wise",
            header: "In Wise",
            width: "minmax(200px,1.3fr)",
            value: (r: Row) => (r.out?.transfer_id ? "Approve and fund in Wise" : (r.out?.error ?? "")),
            cell: (r: Row) => {
              const out = r.out;
              if (!out) return null;
              return (
                <CellControls className="gap-2 text-[12px]">
                  {out.transfer_id ? (
                    <a
                      href={out.url ?? wiseLink(wise.env, out.transfer_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex shrink-0 items-center gap-1 text-accent-text hover:underline"
                    >
                      Approve and fund in Wise <ExternalLink className="size-3" />
                    </a>
                  ) : out.error ? (
                    <span className="truncate text-danger" title={out.error}>
                      {out.error}
                    </span>
                  ) : null}
                  {!out.transfer_id && (out.status === "draft" || out.status === "failed") && (
                    <button type="button" disabled={busy} onClick={() => retry(out.payment_id)} className="shrink-0 text-left text-accent-text hover:underline">
                      {out.status === "draft" ? "Retry (safe: never a second transfer)" : "Try again (a new payment, checked again)"}
                    </button>
                  )}
                </CellControls>
              );
            },
          },
        ]
      : []),
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: (r) => r.out?.amount ?? r.due,
      text: (r) => amount(r.out?.amount ?? r.due, r.bill?.currency ?? "USD"),
      cell: (r) => (
        <span className={cx("font-mono", r.problem && !r.out && "text-faint line-through")}>{amount(r.out?.amount ?? r.due, r.bill?.currency ?? "USD")}</span>
      ),
    },
  ];
  const ok = rows.filter((r) => !r.problem);
  const total = ok.reduce((n, r) => n + r.due, 0);
  const send = async () => {
    setBusy(true);
    try {
      const res = await postJson<{ results: Outcome[] }>("/api/wise/pay", {
        items: ok.map((r) => ({ bill_id: r.id, payment_id: ids.get(r.id) })),
        batch: batch && ok.length > 1 ? { id: batchId, name: `HHL Ops ${planToday()} (${ok.length} bills)` } : null,
      });
      setResults(res.results);
      const made = res.results.filter((r) => r.transfer_id).length;
      if (made) toast.ok(`${made} ${made === 1 ? "transfer" : "transfers"} made in Wise: approve and fund ${made === 1 ? "it" : "them"} in the Wise app`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const retry = async (paymentId: string) => {
    setBusy(true);
    try {
      const res = await postJson<{ results: Outcome[] }>(`/api/wise/payments/${paymentId}/retry`);
      setResults((rs) => (rs ?? []).map((r) => (r.payment_id === paymentId ? res.results[0] : r)));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={results ? "Made in Wise" : `Pay ${billIds.length === 1 ? "a bill" : `${billIds.length} bills`} with Wise`}
      sub={
        results ? (
          "Funding stays in Wise: open each transfer (or the batch) in the Wise app to approve and fund it. HHL Ops marks the bills paid when Wise sends the money."
        ) : (
          <>
            A USD quote and a transfer per bill, to the vendor's Wise recipient. Nothing is sent until someone approves and funds it in the Wise app.
            {wise.env === "sandbox" && <span className="ml-1 font-medium text-warn">Wise sandbox{wise.fake ? " (fake)" : ""}: no real money.</span>}
          </>
        )
      }
      onClose={() => onClose(!!results)}
      width={900}
    >
      {conn?.access === "read_only" && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
          <Lock className="size-3.5" /> The Wise token is read only: it can't create transfers.{" "}
          <Link to="/settings/wise" className="underline">
            Settings → Wise
          </Link>
        </div>
      )}
      <div className="mt-3">
        <DataTable
          fit
          rows={(results ? rows.filter((r) => results.some((x) => x.bill === r.bill?.number)) : rows).map((r) => ({
            ...r,
            out: results?.find((x) => x.bill === r.bill?.number),
          }))}
          columns={payColumns}
          defaultSort={{ key: "order" }}
          exportName="Pay with Wise"
          pageFilters={false}
          rowNoun={["bill", "bills"]}
        />
      </div>
      {!results && (
        <>
          <div className="mt-3 flex items-center gap-3 text-[13px]">
            {ok.length > 1 && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={batch} onChange={(e) => setBatch(e.target.checked)} className="accent-[var(--accent)]" />
                One Wise batch: fund all {ok.length} at once
              </label>
            )}
            <span className="ml-auto text-muted">
              {ok.length} of {rows.length} · <span className="font-mono text-text">{amount(total)}</span> + Wise's fee (shown in Wise)
            </span>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="primary" disabled={!ok.length || busy || conn?.access === "read_only"} onClick={send}>
              {busy ? "Making them in Wise…" : `Create ${ok.length === 1 ? "the transfer" : `${ok.length} transfers`} in Wise`}
            </Button>
          </div>
        </>
      )}
      {results && (
        <div className="mt-4 flex justify-end">
          <Button variant="primary" onClick={() => onClose(true)}>
            Done
          </Button>
        </div>
      )}
    </Dialog>
  );
}

/** A payment that already happened outside HHL Ops: card, wire, other, or a transfer someone made in the Wise app. */
export function PaidOutsideDialog({ billIds: picked, onClose }: { billIds: string[]; onClose: (done: boolean) => void }) {
  const ix = useBillIndex();
  const [billIds] = useState(picked);
  const bills = billIds.map((id) => ix.billById.get(id)).filter((b): b is NonNullable<typeof b> => !!b);
  const [id] = useState(() => crypto.randomUUID());
  const [method, setMethod] = useState<PaymentMethod>("card");
  const [accountId, setAccountId] = useState<string | null>(null); // Which card ---
  const [via, setVia] = useState("");
  const [paidOn, setPaidOn] = useState(planToday());
  const [reference, setReference] = useState("");
  const [transfer, setTransfer] = useState("");
  const [fee, setFee] = useState("");
  const [notes, setNotes] = useState("");
  const [amounts, setAmounts] = useState(() => new Map(bills.map((b) => [b.id, billState(ix, b.id).due.toFixed(2)])));
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const vendors = new Set(bills.map((b) => b.vendor_id));
  const total = bills.reduce((n, b) => n + (numberOf(amounts.get(b.id)) || 0), 0);
  // Table views --- the bills' View and Columns menus, beside the title
  type Row = (typeof bills)[number] & { order: number; vendor: string; due: number };
  // Each bill's amount paid: what it owes to start with, click to change (Enter saves and goes to the next)
  const outsideColumns: Column<Row>[] = [
    {
      key: "order",
      header: "Bill",
      width: "96px",
      text: (b) => b.number,
      cell: (b) => <span className="font-mono text-[12px] font-medium">{b.number}</span>,
    },
    { key: "vendor", header: "Vendor", width: "minmax(120px,1fr)", cell: (b) => <span className="truncate font-medium">{b.vendor}</span> },
    {
      key: "invoice_number",
      header: "Their invoice",
      width: "minmax(100px,1fr)",
      cell: (b) => <span className="truncate font-mono text-[12px] text-muted">{b.invoice_number}</span>,
    },
    {
      key: "due",
      header: "To pay",
      width: "120px",
      align: "right",
      text: (b) => amount(b.due, b.currency),
      cell: (b) => <span className="font-mono text-[12px] text-muted">{amount(b.due, b.currency)}</span>,
    },
    {
      key: "paid",
      header: "Paid",
      headerTitle: "The amount paid on each bill: click to change",
      width: "130px",
      align: "right",
      value: (b) => numberOf(amounts.get(b.id)) || 0,
      text: (b) => amounts.get(b.id) ?? "",
      cell: (b) => {
        const n = numberOf(amounts.get(b.id));
        return Number.isNaN(n) ? <span className="font-mono text-warn">{amounts.get(b.id)}</span> : <span className="font-mono">{amount(n, b.currency)}</span>;
      },
      edit: { get: (b) => amounts.get(b.id) ?? "", save: (b, text) => setAmounts(new Map(amounts).set(b.id, text)) },
    },
  ];
  const save = async () => {
    if (vendors.size > 1) return toast.error("One payment goes to one vendor: record each vendor's separately");
    setBusy(true);
    const { ok } = await mutateWithResult("payment.record", {
      id,
      method,
      paid_on: paidOn,
      reference,
      wise_transfer_id: method === "wise" && /^\d+$/.test(transfer.trim()) ? Number(transfer.trim()) : null,
      fee: numberOf(fee) || 0,
      notes,
      pay_account_id: accountId,
      paid_via: via as never,
      bills: bills.map((b) => ({ bill_id: b.id, amount: numberOf(amounts.get(b.id)) || 0 })),
    });
    if (ok && file) await uploadAttachment({ type: "payment", id }, file, { kind: "receipt", docDate: paidOn }).catch((e) => toast.error((e as Error).message));
    // A Wise transfer's own receipt comes from Wise (a read-only token can fetch it).
    else if (ok && method === "wise" && transfer.trim() && wiseFeature().enabled) await postJson(`/api/wise/payments/${id}/receipt`).catch(() => {});
    setBusy(false);
    if (ok) {
      toast.ok(`Recorded: ${bills.map((b) => b.number).join(", ")} paid by ${PAYMENT_METHOD_LABELS[method].toLowerCase()}`);
      onClose(true);
    }
  };
  return (
    <Dialog
      title="Mark paid outside HHL Ops"
      sub="For a payment that already went: a card (like Bestthing's silicone pads), a bank wire, or a transfer someone made in the Wise app. Nothing is sent."
      onClose={() => onClose(false)}
    >
      {vendors.size > 1 && (
        <div className="mt-3 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">These bills are for different vendors: pick one vendor's.</div>
      )}
      <div className="mt-3">
        <DataTable
          fit
          rows={bills.map((b, order) => ({ ...b, order, vendor: vendorLabelOf(ix.vendorById.get(b.vendor_id)), due: billState(ix, b.id).due }))}
          columns={outsideColumns}
          editing
          defaultSort={{ key: "order" }}
          exportName="Paid outside"
          pageFilters={false}
          rowNoun={["bill", "bills"]}
        />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
        <label className="flex flex-col gap-1">
          <span className="text-muted">How it was paid</span>
          <Select
            value={method}
            options={[...OUTSIDE_METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] })), { value: "wise", label: "Wise (made in the Wise app)" }]}
            onChange={(e) => {
              setMethod(e.target.value as PaymentMethod);
              setAccountId(null);
              setVia("");
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted">Paid on</span>
          <Input type="date" value={paidOn} max={planToday()} onChange={(e) => setPaidOn(e.target.value)} />
        </label>
        <PaidWith
          method={method}
          accountId={accountId}
          via={via}
          onChange={(p) => {
            if (p.accountId !== undefined) setAccountId(p.accountId);
            if (p.via !== undefined) setVia(p.via);
          }}
          className="col-span-2 grid grid-cols-2 gap-x-3"
        />
        <label className="flex flex-col gap-1">
          <span className="text-muted">Reference (their order or transaction number)</span>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 317104" className="font-mono" />
        </label>
        {method === "wise" && (
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-muted">Wise transfer number (optional: then HHL Ops follows it and fetches Wise's receipt)</span>
            <Input value={transfer} inputMode="numeric" onChange={(e) => setTransfer(e.target.value)} placeholder="e.g. 2130206595" className="font-mono" />
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-muted">Fee on top (optional)</span>
          <Input value={fee} inputMode="decimal" onChange={(e) => setFee(e.target.value)} placeholder="0.00" className="font-mono" />
        </label>
        <div className="col-span-2 flex flex-col gap-1">
          <span className="text-muted">Receipt (the card slip, the bank's wire confirmation)</span>
          {/* The shared file box (ui/FileDrop.tsx): a file dropped or pasted anywhere on the dialog is the receipt */}
          <FileDrop
            size="sm"
            accept="application/pdf,image/*"
            multiple={false}
            anywhere
            title={file ? "Another file replaces it" : "The receipt"}
            onFiles={([f]) => setFile(f)}
            chosen={file ? [file] : []}
            onRemove={() => setFile(null)}
          />
        </div>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-muted">Notes</span>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-12" />
        </label>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <span className="mr-auto text-[12px] text-muted">
          Total <span className="font-mono text-text">{amount(total, bills[0]?.currency ?? "USD")}</span>
        </span>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="primary" disabled={busy || !bills.length || vendors.size > 1} onClick={save}>
          Record the payment
        </Button>
      </div>
    </Dialog>
  );
}
